import { ResolvedPageSearchParams } from "../types/AppRouter";
import { UIUtils } from "./UIUtils";

/**
 * Query-param marker a CoverageMigrator (quote-ptz-us, add-a-pet) sets on its one and only exit — success,
 * failure, or nothing-to-do. Its value is the quote id the migrator ran for: a formPage skips the migration only
 * when the marker names the quote it is rendering, so the marker cannot follow the user onto a different quote
 * (the form keeps every search param through step navigation, reset and quote recovery). Lives outside any
 * "use client" module because both server pages read it, and the two forms hand off to each other by URL.
 */
export const SKIP_MIGRATION_PARAM = "skipMigration";

/** The one way the marker is read: by every formPage, and by the Playwright specs waiting for the migrator's exit. */
export const hasSkipMigrationMarkerFor = (searchParams: URLSearchParams | ResolvedPageSearchParams, quoteId: string | undefined): boolean =>
    !!quoteId && UIUtils.getCaseInsensitiveValue(searchParams, SKIP_MIGRATION_PARAM) === quoteId;

/**
 * The one way the marker is written: the URL a migrator leaves through. Every spelling of the key is cleared
 * first because the reader is case-insensitive and takes the last match: a `set` alone would leave a stray
 * `?SkipMigration=<other quote>` after the new value, the reader would see the other quote, and the page would
 * mount the migrator again on every exit.
 */
export const withSkipMigrationMarker = (href: string, quoteId: string): string => {
    const url = new URL(href);
    const keyLower = SKIP_MIGRATION_PARAM.toLowerCase();
    Array.from(new Set(url.searchParams.keys()))
        .filter(key => key.toLowerCase() === keyLower)
        .forEach(key => url.searchParams.delete(key));
    url.searchParams.set(SKIP_MIGRATION_PARAM, quoteId);
    return url.toString();
};
